/**
 * sessionTimeEstimator.js — Calcul déterministe (aucune IA) de la durée réelle d'une séance à
 * partir du tempo, du nombre de séries/répétitions et des temps de repos — utilisé pour le
 * bandeau "temps estimé" en direct du créateur de programme débutant "Choisir moi-même".
 */
const WARMUP_SECONDS = 180;
const TRANSITION_SECONDS = 15; // changement de poste/matériel entre 2 exercices ou blocs
const DEFAULT_SECONDS_PER_REP = 3; // hypothèse par défaut si aucun tempo n'est renseigné

export const parseTempoSeconds = (tempo) => {
  if (!tempo) return null;
  const parts = String(tempo).split(/[-–]/).map((n) => parseFloat(n)).filter((n) => !isNaN(n));
  if (!parts.length) return null;
  return parts.reduce((a, b) => a + b, 0);
};

export const parseAvgReps = (repsScheme, isTimeBased) => {
  if (!repsScheme) return isTimeBased ? 30 : 10;
  const nums = (String(repsScheme).match(/\d+(\.\d+)?/g) || []).map(Number);
  if (!nums.length) return isTimeBased ? 30 : 10;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

export const execSecondsForExercise = (ex) => {
  if (ex.is_time_based) return parseAvgReps(ex.reps_scheme, true);
  const tempoSec = parseTempoSeconds(ex.tempo) || DEFAULT_SECONDS_PER_REP;
  return tempoSec * parseAvgReps(ex.reps_scheme, false);
};

/** Regroupe une liste d'exercices en blocs (superset/bi-set/tri-set/circuit/pré-post-épuisement
 * partagent le même group_id consécutif = 1 seul repos par tour) — même logique que
 * MyProgramBuilder.jsx::blocksOf, dupliquée ici pour rester une fonction pure sans dépendance. */
export const blocksOf = (exercises) => {
  const out = [];
  (exercises || []).forEach((ex, idx) => {
    const last = out[out.length - 1];
    if (ex.group_id && last && last.groupId === ex.group_id) last.indices.push(idx);
    else out.push({ groupId: ex.group_id || null, indices: [idx] });
  });
  return out;
};

export const estimateSessionSeconds = (session) => {
  const exercises = session?.exercises || [];
  if (!exercises.length) return WARMUP_SECONDS;
  let total = WARMUP_SECONDS;
  blocksOf(exercises).forEach((block) => {
    const blockExs = block.indices.map((i) => exercises[i]);
    if (blockExs.length > 1) {
      const rounds = Math.max(...blockExs.map((e) => e.sets_count || 1));
      const perRound = blockExs.reduce((sum, e) => sum + execSecondsForExercise(e), 0) + (blockExs[0].rest_seconds ?? 60);
      total += rounds * perRound + TRANSITION_SECONDS;
    } else {
      const e = blockExs[0];
      const sets = e.sets_count || 1;
      total += sets * (execSecondsForExercise(e) + (e.rest_seconds ?? 60)) + TRANSITION_SECONDS;
    }
  });
  return Math.round(total);
};

export const estimateSessionMinutes = (session) => Math.round(estimateSessionSeconds(session) / 60);

export const formatMinutes = (min, en = false) => `${min} ${en ? 'min' : 'min'}`;

/** null si dans la marge (±5 min), sinon { type: 'too_long'|'too_short', diffMinutes } */
export const suggestAdjustment = (session, targetMinutes) => {
  if (!targetMinutes) return null;
  const est = estimateSessionMinutes(session);
  const diff = est - Number(targetMinutes);
  if (Math.abs(diff) <= 5) return null;
  return diff > 0 ? { type: 'too_long', diffMinutes: diff, est } : { type: 'too_short', diffMinutes: -diff, est };
};

/** Réduit automatiquement la séance pour rentrer dans la marge cible : retire d'abord 1 série
 * sur les exercices qui en ont >2, puis en dernier recours retire le dernier exercice isolé
 * (jamais le 1er exercice ni un bloc lié en dessous de sa taille minimale). Mutation in-place
 * sur une COPIE fournie par l'appelant (le composant appelant doit cloner `session` avant). */
export const autoShrinkSession = (session, targetMinutes) => {
  const targetSeconds = Number(targetMinutes) * 60;
  let guard = 0;
  while (estimateSessionSeconds(session) > targetSeconds * 1.08 && guard < 30) {
    const exercises = session.exercises;
    let reduced = false;
    for (let i = exercises.length - 1; i >= 0; i -= 1) {
      if ((exercises[i].sets_count || 0) > 2) { exercises[i].sets_count -= 1; reduced = true; break; }
    }
    if (!reduced) {
      if (exercises.length > 1) exercises.pop();
      else break;
    }
    guard += 1;
  }
  return session;
};
